/**
 * The exercise-type taxonomy.
 *
 * Ported from `move-with-me-frontend/.../features/exercises/model/exerciseType.ts`, **without its
 * colour map and without its glyph names**. On the phone that module carries five KANON brand
 * colours as data plus two glyph registry keys per type; here the colours belong to the token layer
 * and the glyphs to whichever component draws one. What survives is the part that is genuinely
 * domain: which types exist, and how the wire spells them.
 *
 * The human LABEL is chrome and comes from i18n through `exerciseTypeLabelKey` — never hardcoded,
 * and never carried on the model.
 */

/**
 * Every exercise type the backend names — SEVEN, matching `ExerciseType` in
 * `train-with-me/Data/Enums/ExerciseType.cs` one for one.
 *
 * Mobile's union has five and omits `Custom`. That is a gap rather than a decision: the backend
 * emits it, and a build that does not know a type resolves it to `null`, so a custom exercise
 * loses its type badge and drops out of every type facet. Ported with the sixth member present.
 *
 * **`plyometric` joined on 9 September 2026, and it was the same gap one member further on.** The
 * backend has emitted `Plyometric` since the plyometric-category round; this build had never heard
 * of it, so every plyometric movement in the catalogue carried `typeKey: null` — no type chip on
 * its row, and absent from the type facet, which is exactly the silent narrowing the `Custom` note
 * above describes. The 07.09 programator mock draws `Plyometryczne` as one of its six type pills,
 * which is what surfaced it. `exerciseType` is a bare `string` on the wire (no enum in
 * `openapi.json`), so nothing generated would have caught this.
 */
export type ExerciseTypeKey =
  | 'strength'
  | 'bodyweight'
  | 'cardio'
  | 'mobility'
  | 'conditioning'
  | 'plyometric'
  | 'custom';

/**
 * The app's key → the backend's PascalCase enum value. Exhaustive over the union, so a new type
 * cannot be added without teaching the wire mapping.
 */
const TO_API: Record<ExerciseTypeKey, string> = {
  strength: 'Strength',
  bodyweight: 'Bodyweight',
  cardio: 'Cardio',
  mobility: 'Mobility',
  conditioning: 'Conditioning',
  plyometric: 'Plyometric',
  custom: 'Custom',
};

/** Stable display and filter order — the order KANON draws its type tabs in. */
export const EXERCISE_TYPE_KEYS: readonly ExerciseTypeKey[] = Object.keys(TO_API) as ExerciseTypeKey[];

/** One backend value → a known key, or `null` when this build has never heard of it. */
export function exerciseTypeFromApi(value: string | null | undefined): ExerciseTypeKey | null {
  if (!value) return null;
  const key = value.trim().toLowerCase();
  return (EXERCISE_TYPE_KEYS as readonly string[]).includes(key) ? (key as ExerciseTypeKey) : null;
}

/** One key → the backend's PascalCase enum value. */
export function exerciseTypeToApi(key: ExerciseTypeKey): string {
  return TO_API[key];
}

/**
 * The i18n key for a type's display label, in the upstream `exerciseLibrary.type.*` group.
 *
 * **The upstream group carries FIVE of the six.** `exerciseLibrary.type.custom` does not exist in
 * either copied locale file, because mobile's own union never had `custom` to label. Those files
 * are a verbatim copy and are never edited here, so the sixth label has to come from the `web`
 * namespace — and the first surface to render a type facet or a type badge is the one that must
 * supply it. Nothing renders one today; `EXERCISE_TYPE_KEYS` has no consumer yet.
 *
 * PR-C owns that surface. Mixing namespaces inside one legend is the failure execution ADR §7
 * names, so the fix is the whole group moving to `web:`, not one key borrowed from it.
 */
export function exerciseTypeLabelKey(key: ExerciseTypeKey): string {
  return `exerciseLibrary.type.${key}`;
}
